import Image from 'next/image'
import Link from 'next/link'
import { useRouter } from 'next/router'
import { Container } from 'react-grid-system'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faPhoneVolume, faMap } from '@fortawesome/free-solid-svg-icons'
import { Heading, SocialMediaRow } from '~elements'
import { FooterProps, NavigationMenuItemProps } from '~types'
import { fireAnalyticsEvent } from '~analyticsUtils'
import styles from './Footer.module.scss'
import { useWindowSize } from 'utils/hooks'
import { NEXT_IMAGE_QUALITY, ONE_TRUST_TITLE, SLUG_SHOWS_CALENDAR } from '~constants'
import PrivacyIcon from 'public/assets/PrivacyIcon.png'
import TicketmasterLogo from 'public/assets/ticketmaster.svg'
import { Fragment, useEffect, useState } from 'react'
import ImageLinkGroup from '../ImageLinkGroup/ImageLinkGroup'
import cx from 'classnames'

const Footer = ({
  generalInformation,
  legalText,
  logoPrimary,
  logoSecondary,
  navMenu1,
  navMenu2,
  navMenu3,
  navMenuLegal,
  socialMediaRow,
  socialMediaRowHeadline,
  imageLinkGroup
}: FooterProps) => {
  const { asPath } = useRouter()
  const { isBreakpoint: isBelowBreakpointMd } = useWindowSize('md', '<')
  const linkModule = 'global_footer'
  const linkLocation = 'footer'
  const [showDisclaimer, setShowDisclaimer] = useState(false)

  const findReplaceDynamicYear = (text: string): string => {
    return text.includes('${YEAR}') ? text.replace('${YEAR}', new Date().getFullYear().toString()) : text
  }

  const handleAnalyticsEvent = (url: string, title: string) => {
    fireAnalyticsEvent(url, asPath, {
      event: 'click_nav',
      clickTracking: `${linkModule}:${title}`,
      linkLocation: linkLocation,
      linkModule: linkModule,
      navType: 'footer'
    })
  }

  useEffect(() => {
    setShowDisclaimer(asPath === SLUG_SHOWS_CALENDAR)
  }, [asPath])

  const footerNavMenu = (menuItems: NavigationMenuItemProps[], headline: string) => {
    return (
      <>
        {menuItems && menuItems.length ? (
          <>
            {headline && (
              <Heading level={6} levelDisplay='eyebrow' color='light'>
                {headline}
              </Heading>
            )}
            <ul className={styles['column__content']}>
              {menuItems &&
                menuItems.map(
                  ({ url, isTargetBlank, title }: NavigationMenuItemProps, navIndex: number) =>
                    url && (
                      <Fragment key={navIndex}>
                        {title === ONE_TRUST_TITLE ? (
                          <li key={navIndex}>
                            <a
                              className='ot-sdk-show-settings'
                              onClick={() => {
                                handleAnalyticsEvent(url, title)
                                // @ts-ignore
                                window?.OneTrust && window.OneTrust.ToggleInfoDisplay()
                              }}
                            >
                              Your Privacy Choices
                            </a>
                            <Image
                              src={PrivacyIcon}
                              alt='privacy icon'
                              width='30'
                              height='14'
                              quality={NEXT_IMAGE_QUALITY}
                            />
                          </li>
                        ) : (
                          <li key={navIndex}>
                            <Link
                              href={url}
                              target={isTargetBlank ? '_blank' : '_self'}
                              onClick={() => handleAnalyticsEvent(url, title)}
                            >
                              {findReplaceDynamicYear(title)}
                            </Link>
                          </li>
                        )}
                      </Fragment>
                    )
                )}
            </ul>
          </>
        ) : null}
      </>
    )
  }

  const phoneNumberHref = generalInformation?.phoneNumber ? `tel:+1${generalInformation.phoneNumber}` : ''

  return (
    <>
      {showDisclaimer && (
        <div className={styles['disclaimer']}>
          Ticket purchase available through
          <div className={styles['ticketmaster-icon']}>
            <TicketmasterLogo />
          </div>
        </div>
      )}
      <footer className={cx(styles['footer-global'], !showDisclaimer && styles.hasGradient)}>
        <Container className={'container container--width-100'}>
          <div className={styles['row']}>
            <div className={styles['column-1']}>
              {socialMediaRowHeadline ? <p className={styles['column__header']}>{socialMediaRowHeadline}</p> : null}
              <SocialMediaRow align={isBelowBreakpointMd ? 'center' : 'left'} socialMediaRow={socialMediaRow} />
            </div>
            <div className={styles['column-2']}>
              <div className={styles['column-2__row']}>
                {generalInformation && (
                  <div className={styles['column-2__column']}>
                    <Heading level={6} levelDisplay='eyebrow' color='light'>
                      General Information
                    </Heading>
                    <div className={styles['column__content']}>
                      <p className={styles['contact-info']}>
                        <FontAwesomeIcon icon={faMap} />
                        {generalInformation.addressLine1}
                        {isBelowBreakpointMd ? <br /> : ', '}
                        {generalInformation.addressLine2}
                      </p>
                      {phoneNumberHref && (
                        <p className={styles['contact-info']}>
                          <a onClick={() => handleAnalyticsEvent('', phoneNumberHref)} href={phoneNumberHref}>
                            <FontAwesomeIcon icon={faPhoneVolume} />
                            {generalInformation.phoneNumber}
                          </a>
                        </p>
                      )}
                    </div>
                  </div>
                )}
                <div className={styles['column-2__column']}>{footerNavMenu(navMenu1, 'Partner With Us')}</div>
                <div className={styles['column-2__column']}>{footerNavMenu(navMenu2, 'Resources')}</div>
                <div className={styles['column-2__column']}>{footerNavMenu(navMenu3, 'About Us')}</div>
              </div>
            </div>
          </div>
        </Container>

        {imageLinkGroup && (
          <Container className={'container container--width-100'}>
            <div className={styles['footer-global__bottom_partnerships']}>
              <ImageLinkGroup {...imageLinkGroup} linkLocation={linkLocation} />
            </div>
          </Container>
        )}

        <Container className={'container container--width-100'}>
          <div className={styles['footer-global__bottom']}>
            <div className={styles['bottom__logos']}>
              <div className={styles['logos-row']}>
                {logoPrimary && logoPrimary.url ? (
                  <div className={styles['logo']}>
                    <div className={'image-container'}>
                      <Image
                        src={logoPrimary.url}
                        alt='Sphere partner'
                        width='0'
                        height='0'
                        sizes='100vw'
                        quality={NEXT_IMAGE_QUALITY}
                      />
                    </div>
                  </div>
                ) : null}
                {logoSecondary && logoSecondary.url ? (
                  <div className={styles['logo']}>
                    <div className={'image-container'}>
                      <Image
                        src={logoSecondary.url}
                        alt='Sphere partner'
                        width='0'
                        height='0'
                        sizes='100vw'
                        quality={NEXT_IMAGE_QUALITY}
                      />
                    </div>
                  </div>
                ) : null}
              </div>
            </div>
            <div className={styles['bottom__nav']}>
              {footerNavMenu(navMenuLegal, '')}
              {legalText ? <small>{findReplaceDynamicYear(legalText)}</small> : ''}
            </div>
          </div>
        </Container>
      </footer>
    </>
  )
}

export default Footer
